/**
 * Login is a web-app concern (the AuthService contract intentionally has no
 * signIn). We act on the same Firebase app `@praesto/core` initialised,
 * setting `auth.tenantId = slug` BEFORE sign-in so the user authenticates
 * against their own Identity Platform tenant (apps/web CLAUDE.md rule 5).
 */
import { type FirebaseApp, getApp, getApps, initializeApp } from "firebase/app";
import {
  type ActionCodeInfo,
  applyActionCode,
  checkActionCode,
  confirmPasswordReset,
  getAuth,
  signInWithCustomToken,
  signInWithEmailAndPassword,
  verifyPasswordResetCode,
} from "firebase/auth";
import { getEnv } from "./env";
import {
  AppCheckRefusedError,
  appCheckHeader,
  createAppCheckTokenSource,
  isAppCheckRefusal,
} from "./app-check";

/**
 * Get the shared "praesto" Firebase app, initializing it from env if it does
 * not exist yet. Most routes reach a tenant path first, where `getCore()`
 * (in @praesto/core) already created the app — but the Access Hub renders at
 * the root BEFORE any `getCore()` call, so `getApp("praesto")` would throw.
 * The Firebase app is tenant-agnostic (the config carries no slug; the tenant
 * is set per-call via `auth.tenantId`), so a find-or-init here is safe and
 * dedupes against the app `getCore()` creates.
 */
function praestoApp(): FirebaseApp {
  const existing = getApps().find((a) => a.name === "praesto");
  if (existing) return existing;
  const { firebase } = getEnv();
  return initializeApp(
    {
      apiKey: firebase.apiKey,
      authDomain: firebase.authDomain,
      projectId: firebase.projectId,
      appId: firebase.appId,
      storageBucket: firebase.storageBucket,
      messagingSenderId: firebase.messagingSenderId,
    },
    "praesto",
  );
}

/**
 * App Check token for our own /api calls (signup, password reset, assistant —
 * C-16), minted on the same "praesto" app. `null` when App Check is off or the
 * token can't be had; see `./app-check.ts`.
 */
export const getAppCheckToken = createAppCheckTokenSource({
  app: praestoApp,
  siteKey: () => getEnv().appCheckSiteKey,
});

export async function signIn(
  tenantSlug: string,
  email: string,
  password: string,
): Promise<void> {
  const auth = getAuth(getApp("praesto"));
  auth.tenantId = tenantSlug;
  const cred = await signInWithEmailAndPassword(auth, email, password);
  // Force a token refresh before anyone reads Firestore.
  //
  // The token Identity Platform hands back at sign-in does not always carry the
  // custom claims yet (tenantId, globalRole, hotelRoles). Every security rule
  // keys off those, so the first reads — the user doc AND the settings snapshot
  // listener — come back permission-denied. The user doc failure is handled,
  // but the settings listener simply never resolves, and the boot gate waits
  // for it forever: that is the "Avvio in corso…" that a refresh cures.
  //
  // Costs one round-trip on sign-in, and only on sign-in.
  await cred.user.getIdToken(true);
}

/**
 * Send a password-reset email for `email` in the given tenant. Tenant-aware:
 * `auth.tenantId = slug` BEFORE the call so Identity Platform issues the reset
 * against the right tenant (same rule as `signIn`).
 *
 * The caller MUST NOT surface whether the address exists — this throws
 * `auth/user-not-found` for unknown emails, and the UI swallows it and shows a
 * generic "if it exists, we sent a link" message (no account enumeration).
 *
 * `continueUrl` is where Identity Platform sends the user after the reset
 * completes; it must be on an authorized domain.
 */
export async function sendPasswordReset(
  tenantSlug: string,
  email: string,
  _continueUrl: string,
): Promise<void> {
  // The reset email now goes through our own SES sender (brand-consistent with
  // every other transactional email), not Firebase's default. The backend
  // (`signupApi` `/api/signup/password-reset`, same-origin via the Hosting
  // rewrite) generates the tenant-scoped reset link and mails it. It ALWAYS
  // responds generically (no account enumeration), so the UI shows the same
  // "if it exists, we sent a link" message regardless. `_continueUrl` is now
  // built server-side (`/{tenantSlug}/login`).
  const res = await fetch("/api/signup/password-reset", {
    method: "POST",
    headers: {
      "content-type": "application/json",
      ...appCheckHeader(await getAppCheckToken()),
    },
    body: JSON.stringify({ email, tenantSlug, locale: "it" }),
  });
  // An App Check refusal says nothing about the address (it comes before any
  // lookup), so it can be told apart: the user must know no email is coming.
  if (await isAppCheckRefusal(res)) throw new AppCheckRefusedError();
  // Resolve on 2xx/4xx (a 429/400 still shows the generic "sent" message, which
  // must not double as an existence probe). Surface only a real outage (5xx).
  if (res.status >= 500) throw new Error("password_reset_failed");
}

/**
 * Validate a reset `oobCode` from the email link and return the account email it
 * belongs to (shown on the reset form). Throws on an invalid/expired code.
 * `tenantId` comes from the email-action URL (Identity Platform appends it).
 */
export async function verifyResetCode(tenantId: string, oobCode: string): Promise<string> {
  // `praestoApp()`, not `getApp`: on `/auth/action` without a `tenantId` no
  // `getCore()` ran, so the app may not exist yet. An empty id means a
  // project-level account (no Identity Platform tenant), hence `null`.
  const auth = getAuth(praestoApp());
  auth.tenantId = tenantId || null;
  return verifyPasswordResetCode(auth, oobCode);
}

/** Set a new password using a verified reset `oobCode`. */
export async function confirmReset(
  tenantId: string,
  oobCode: string,
  newPassword: string,
): Promise<void> {
  const auth = getAuth(praestoApp());
  auth.tenantId = tenantId || null;
  await confirmPasswordReset(auth, oobCode, newPassword);
}

/**
 * Validate a non-reset email-action `oobCode` (verifyEmail, recoverEmail,
 * verifyAndChangeEmail, revertSecondFactorAddition) and return what it is about
 * — `data.email` is the address the action concerns. Throws on an
 * invalid/expired/used code. `tenantId` as in `verifyResetCode`.
 */
export async function inspectActionCode(tenantId: string, oobCode: string): Promise<ActionCodeInfo> {
  const auth = getAuth(praestoApp());
  auth.tenantId = tenantId || null;
  return checkActionCode(auth, oobCode);
}

/** Consume a non-reset email-action `oobCode`. Single-use: a second call throws. */
export async function applyEmailActionCode(tenantId: string, oobCode: string): Promise<void> {
  const auth = getAuth(praestoApp());
  auth.tenantId = tenantId || null;
  await applyActionCode(auth, oobCode);
}

/**
 * Sign in against a specific Identity Platform tenant using a custom token.
 * Uses `praestoApp()` (not `getApp`) so it works on the Access Hub route,
 * where the Firebase app has not been created by `getCore()` yet.
 */
export async function signInToTenant(tenantId: string, customToken: string): Promise<void> {
  const auth = getAuth(praestoApp());
  auth.tenantId = tenantId;
  await signInWithCustomToken(auth, customToken);
}

/**
 * Sign in to a target tenant with a Mademind-issued custom token
 * (impersonation flow). The custom token already carries `tenantId` in
 * its claims; we still set `auth.tenantId` explicitly so Firebase's
 * tenant-aware verification accepts the token.
 */
export async function signInWithImpersonationToken(
  targetTenantId: string,
  customToken: string,
): Promise<void> {
  return signInToTenant(targetTenantId, customToken);
}
